import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Box, Button, Tooltip, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { isProductionEnvironment } from 'utils/environment';

type Props = {
  onPrev: () => void;
  onNext: () => void;
  onSkip?: () => void;
  onSave?: () => void;
  disablePrev?: boolean;
  disableNext?: boolean;
  disableSave?: boolean;
  nextLabel?: string;
  nextIcon?: React.ReactNode;
};

const StepButton = styled(Button)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  '&.Mui-disabled': {
    color: theme.palette.grey[500],
    borderColor: theme.palette.grey[400],
    backgroundColor: 'transparent',
  },
}));

const StepFooter: React.FC<Props> = ({ onPrev, onNext, onSkip, onSave, disablePrev, disableNext, disableSave, nextLabel, nextIcon }) => {
  const theme = useTheme();
  const isRtl = theme.direction === 'rtl';

  return (
    <Box display="flex" justifyContent="space-between" alignItems="center">
      <StepButton onClick={onPrev} disabled={disablePrev} variant="outlined">
        {isRtl ? <ChevronRight /> : <ChevronLeft />}
        <FormattedMessage id="general.previous" />
      </StepButton>
      {!isProductionEnvironment && !!onSkip && (
        <Tooltip title="For testing purposes only!" placement="top">
          <StepButton variant="text" onClick={onSkip}>
            Continue without submission
          </StepButton>
        </Tooltip>
      )}
      <Box display="flex" flexDirection="row" justifyContent="flex-end" alignItems="center" gap={2}>
        {!disableSave && (
          <StepButton onClick={onSave} disabled={disableSave} variant={disableSave ? 'outlined' : 'contained'}>
            <FormattedMessage id="general.save" />
          </StepButton>
        )}

        <StepButton onClick={onNext} disabled={disableNext} variant={disableNext ? 'outlined' : 'contained'}>
          {nextLabel ?? <FormattedMessage id="general.next" />}
          {nextIcon ?? (isRtl ? <ChevronLeft /> : <ChevronRight />)}
        </StepButton>
      </Box>
        
    </Box>
  );
};

export default StepFooter;
